import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { tapHaptic } from '../../../shared/lib/haptics';
import { Button } from '../../../shared/ui/Button';
import { Label } from '../../../shared/ui/Label';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { Select } from '../../../shared/ui/Select';
import { Skeleton } from '../../../shared/ui/Skeleton';
import { StatusBanner } from '../../../shared/ui/StatusBanner';
import { useToast } from '../../../shared/ui/toast-context';
import {
  createSection,
  getTest,
  listSections,
  QBANK_QUERY_KEYS,
  updateTest,
  type SectionKind,
  type TestSectionRow,
} from '../tests-api';

import s from './TestEditorPage.module.css';

function sectionLabel(kind: SectionKind): string {
  switch (kind) {
    case 'listening':
      return 'Listening';
    case 'reading':
      return 'Reading';
    case 'writing':
      return 'Writing';
    case 'speaking':
      return 'Speaking';
  }
}

function sortByDisplayOrder(a: TestSectionRow, b: TestSectionRow): number {
  return a.display_order - b.display_order;
}

export function AdminTestEditorPage(): JSX.Element {
  const { testId = '' } = useParams<{ testId: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  const testQuery = useQuery({
    queryKey: QBANK_QUERY_KEYS.test(testId),
    queryFn: () => getTest(testId),
    enabled: testId !== '',
  });
  const sectionsQuery = useQuery({
    queryKey: QBANK_QUERY_KEYS.sections(testId),
    queryFn: () => listSections(testId),
    enabled: testId !== '',
  });

  const publishMutation = useMutation({
    mutationFn: () =>
      updateTest({
        testId,
        patch: { status: testQuery.data?.status === 'published' ? 'draft' : 'published' },
      }),
    onSuccess: (row) => {
      toast.show(`Test status set to ${row.status}.`, { tone: 'success' });
      tapHaptic();
      void queryClient.invalidateQueries({ queryKey: QBANK_QUERY_KEYS.test(testId) });
      void queryClient.invalidateQueries({ queryKey: QBANK_QUERY_KEYS.tests });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const addSectionMutation = useMutation({
    mutationFn: (kind: SectionKind) =>
      createSection({
        testId,
        kind,
        displayOrder: (sectionsQuery.data?.length ?? 0) + 1,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QBANK_QUERY_KEYS.sections(testId) });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const sections = (sectionsQuery.data ?? []).slice().sort(sortByDisplayOrder);
  const existingKinds = new Set(sections.map((sec) => sec.kind));
  const availableKinds = (['listening', 'reading', 'writing', 'speaking'] as const).filter(
    (k) => !existingKinds.has(k),
  );

  if (testQuery.isPending) {
    // Shaped like the loaded page: crumb, title block, then the sections card.
    return (
      <div className={s.root} role="status" aria-label="Loading test">
        <Skeleton width={112} height={14} />
        <div className={s.titleSkeleton}>
          <Skeleton width={88} height={12} />
          <Skeleton width="65%" height={36} />
          <Skeleton width={160} height={16} />
        </div>
        <Skeleton height={200} radius="xl" />
      </div>
    );
  }
  if (testQuery.error !== null || !testQuery.data) {
    return (
      <div className={s.root}>
        <StatusBanner tone="error">
          Could not load test: {testQuery.error?.message ?? 'not found'}
        </StatusBanner>
      </div>
    );
  }
  const test = testQuery.data;

  return (
    <div className={s.root}>
      <nav className={s.crumbs} aria-label="Breadcrumbs">
        <Link to="/admin/tests" className={s.crumb}>
          ← Global tests
        </Link>
      </nav>

      <PageHeader
        eyebrow={test.exam_type === 'academic' ? 'Academic' : 'General Training'}
        title={test.title}
        subtitle={
          <>
            {test.total_duration_minutes} minutes ·{' '}
            <strong>
              {test.status === 'published'
                ? 'Published'
                : test.status === 'draft'
                  ? 'Draft'
                  : 'Archived'}
            </strong>
          </>
        }
        actions={
          <Button
            type="button"
            variant={test.status === 'published' ? 'secondary' : 'primary'}
            loading={publishMutation.isPending}
            onClick={() => publishMutation.mutate()}
          >
            {test.status === 'published' ? 'Unpublish' : 'Publish'}
          </Button>
        }
      />

      {serverError !== null ? <StatusBanner tone="error">{serverError}</StatusBanner> : null}

      <section className={s.section}>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>Sections</h2>
          <span className={s.sectionMeta}>{sections.length} of 4</span>
        </div>
        {sections.length === 0 ? (
          <p className={s.empty}>
            No sections yet. Add Listening, Reading, Writing, and Speaking below.
          </p>
        ) : (
          <ul className={s.list}>
            {sections.map((sec) => (
              <li key={sec.id} className={s.row}>
                <span className={s.kindPill}>{sectionLabel(sec.kind)}</span>
                <span className={s.rowMeta}>{sec.duration_minutes} min</span>
                <Link to={`/admin/tests/${testId}/sections/${sec.id}`} className={s.manage}>
                  Edit section →
                </Link>
              </li>
            ))}
          </ul>
        )}

        {availableKinds.length > 0 ? (
          <AddSectionRow
            available={availableKinds}
            disabled={addSectionMutation.isPending}
            onAdd={(kind) => addSectionMutation.mutate(kind)}
          />
        ) : null}
      </section>
    </div>
  );
}

interface AddSectionRowProps {
  available: readonly SectionKind[];
  disabled: boolean;
  onAdd: (kind: SectionKind) => void;
}

function AddSectionRow({ available, disabled, onAdd }: AddSectionRowProps): JSX.Element {
  const [choice, setChoice] = useState<SectionKind>(available[0] ?? 'listening');

  // Keep the picker valid when sections are added/removed elsewhere.
  useEffect(() => {
    if (!available.includes(choice) && available.length > 0) {
      setChoice(available[0] ?? 'listening');
    }
  }, [available, choice]);

  return (
    <div className={s.addRow}>
      <Label htmlFor="add-kind">Add section</Label>
      <Select
        id="add-kind"
        value={choice}
        disabled={disabled}
        onChange={(e) => setChoice(e.target.value as SectionKind)}
      >
        {available.map((k) => (
          <option key={k} value={k}>
            {sectionLabel(k)}
          </option>
        ))}
      </Select>
      <Button type="button" onClick={() => onAdd(choice)} loading={disabled}>
        Add
      </Button>
    </div>
  );
}
